import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, useApp } from "@/context/AppContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RouteCard } from "@/components/RouteCard";
import { BundlesSection } from "@/components/BundlesSection";
import { CardCarousel } from "@/components/CardCarousel";
import { DifficultyFilter, CategoryFilter } from "@/components/Filters";
import { MapPin, CalendarDays, Users, Award, Search } from "lucide-react";
import { QuoteDialog } from "@/components/QuoteDialog";
import { InstagramFeed } from "@/components/InstagramFeed";
import { NewsBlock } from "@/components/NewsBlock";
import { MexicoMap } from "@/components/MexicoMap";
import { money, thumb, srcSet } from "@/lib/helpers";

const BAND = "https://images.unsplash.com/photo-1660314002642-77bbfe3e9b48?crop=entropy&cs=srgb&fm=jpg&q=85";


export default function Home() {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [summits, setSummits] = useState([]);
  const [adventures, setAdventures] = useState([]);
  const [weekend, setWeekend] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [news, setNews] = useState([]);
  const [reviews, setReviews] = useState(null);
  const [social, setSocial] = useState(null);
  const [settings, setSettings] = useState(null);
  const [promos, setPromos] = useState([]);
  const [promoClosed, setPromoClosed] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [diff, setDiff] = useState("todos");
  const [cat, setCat] = useState("todos");
  const [form, setForm] = useState({ q: "", date: "", people: 1, difficulty: "todos" });

  useEffect(() => {
    api.get("/routes", { params: { category: "cumbre", difficulty: diff } }).then((r) => setSummits(r.data));
  }, [diff]);

  useEffect(() => {
    api.get("/routes", { params: { category: cat === "todos" ? undefined : cat } })
      .then((r) => setAdventures(r.data.filter((x) => cat !== "todos" || x.category !== "cumbre")));
  }, [cat]);

  useEffect(() => {
    api.get("/routes/weekend").then((r) => setWeekend(r.data.routes));
    api.get("/routes/weekdays").then((r) => setWeekdays(r.data.routes));
    api.get("/routes/novedades").then((r) => setNews(r.data.routes));
    api.get("/reviews").then((r) => setReviews(r.data));
    api.get("/social").then((r) => setSocial(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/promos/active").then((r) => {
      setPromos(r.data.promos);
      const seen = localStorage.getItem("zenda_promo_seen");
      setPromoClosed(Boolean(r.data.promos[0] && seen === r.data.promos[0].promo_id));
    });
  }, []);

  const explore = () => {
    const p = new URLSearchParams();
    if (form.q) p.set("q", form.q);
    if (form.date) p.set("date", form.date);
    if (form.difficulty !== "todos") p.set("difficulty", form.difficulty);
    toast.success(lang === "es" ? "Buscando aventuras..." : "Searching adventures...");
    nav(`/tours?${p.toString()}`);
  };

  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      <Navbar />

      {promos.length > 0 && !promoClosed && (
        <div className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4 pointer-events-none">
          <div className="relative w-full max-w-md rounded-2xl bg-[#16221D] text-white p-5 pr-12 shadow-[0_20px_50px_rgb(0,0,0,0.3)] animate-rise pointer-events-auto"
            data-testid="promo-notification">
            <button onClick={() => { localStorage.setItem("zenda_promo_seen", promos[0].promo_id); setPromoClosed(true); }}
              aria-label={lang === "es" ? "Cerrar promoción" : "Close promo"}
              data-testid="promo-close-btn"
              className="absolute top-2 right-2 h-10 w-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-300">✕</button>
          <p className="text-[11px] uppercase tracking-widest text-[#F0C674]">
            {promos[0].kind === "coupon" ? (lang === "es" ? "Cupón disponible" : "Coupon available") : (lang === "es" ? "Promoción activa" : "Active promo")}
          </p>
          <p className="mt-2 font-semibold text-base">
            {(lang === "en" && promos[0].banner_text_en) || promos[0].banner_text || promos[0].name}
          </p>
          <p className="mt-1 text-sm text-white/75">
            {promos[0].discount_type === "percent" ? `${promos[0].value}% ` : `${money(promos[0].value)} `}
            {lang === "es" ? "de descuento" : "off"}
            {promos[0].min_participants > 1 && (lang === "es" ? ` desde ${promos[0].min_participants} personas` : ` from ${promos[0].min_participants} people`)}
            {promos[0].ends_at && (lang === "es" ? ` · hasta ${promos[0].ends_at}` : ` · until ${promos[0].ends_at}`)}
          </p>
          {promos[0].code && (
            <p className="mt-3 inline-block rounded-lg border border-dashed border-[#F0C674] px-3 py-1.5 text-sm font-mono tracking-widest" data-testid="promo-code">
              {promos[0].code}
            </p>
          )}
          </div>
        </div>
      )}

      <section className="relative min-h-[92vh] flex items-center" data-testid="hero-section">
        <img src={thumb(settings?.hero_image || BAND, 1280)} alt=""
          srcSet={srcSet(settings?.hero_image || BAND)} sizes="100vw"
          fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#16221D]/85 via-[#16221D]/35 to-[#16221D]/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-16">
          <h1 className="font-heading text-white uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl animate-rise">
            {t.hero.title}
          </h1>
          <p className="mt-5 text-white/85 text-base md:text-lg max-w-xl animate-rise" style={{ animationDelay: "120ms" }}>
            {t.hero.subtitle}
          </p>

          <div className="mt-12 rounded-3xl bg-white/95 backdrop-blur-xl border border-white/60 p-5 md:p-6 shadow-[0_18px_50px_rgb(0,0,0,0.18)] animate-rise" style={{ animationDelay: "240ms" }}>
            <p className="text-sm font-semibold text-[#16221D] mb-4">{t.hero.searchTitle}</p>
            <div className="grid gap-4 md:grid-cols-[1.4fr_1fr_0.9fr_1fr_auto] md:items-end">
              <label className="block">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#8A968E]"><MapPin className="h-3.5 w-3.5" />{t.hero.destination}</span>
                <input data-testid="search-destination" value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })}
                  placeholder={t.hero.destinationPh}
                  className="mt-1 w-full bg-transparent border-b border-[#E2DFD7] pb-2 text-sm outline-none focus:border-[#6E7E56]" />
              </label>
              <label className="block">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#8A968E]"><CalendarDays className="h-3.5 w-3.5" />{t.hero.date}</span>
                <input data-testid="search-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1 w-full bg-transparent border-b border-[#E2DFD7] pb-2 text-sm outline-none focus:border-[#6E7E56]" />
              </label>
              <label className="block">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#8A968E]"><Users className="h-3.5 w-3.5" />{t.hero.people}</span>
                <input data-testid="search-people" type="number" min="1" max="20" value={form.people}
                  onChange={(e) => setForm({ ...form, people: e.target.value })}
                  className="mt-1 w-full bg-transparent border-b border-[#E2DFD7] pb-2 text-sm outline-none focus:border-[#6E7E56]" />
              </label>
              <label className="block">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#8A968E]"><Award className="h-3.5 w-3.5" />{t.hero.difficulty}</span>
                <select data-testid="search-difficulty" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                  className="mt-1 w-full bg-transparent border-b border-[#E2DFD7] pb-2 text-sm outline-none focus:border-[#6E7E56]">
                  {["todos", "familiar", "principiante", "intermedio", "avanzado", "muy_avanzado"].map((d) => (
                    <option key={d} value={d}>{t.difficulty[d]}</option>
                  ))}
                </select>
              </label>
              <button onClick={explore} data-testid="explore-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D94E28] text-white font-semibold px-8 py-3 hover:bg-[#B83B1A] transition-colors duration-300">
                <Search className="h-4 w-4" />{t.hero.explore}
              </button>
            </div>
          </div>
        </div>
      </section>

      {(() => {
        const order = settings?.section_order || ["weekend", "news", "weekdays", "summits", "adventures", "bundles"];
        const blocks = {
          weekend: weekend.length > 0 && (
            <Section key="weekend" title={t.sections.weekend} sub={t.sections.weekendSub} testid="weekend-section"
              items={weekend} link="/fin-de-semana" more={t.sections.more} availability />
          ),
          weekdays: weekdays.length > 0 && (
            <Section key="weekdays" title={t.sections.weekdays} sub={t.sections.weekdaysSub} testid="weekdays-section"
              items={weekdays} link="/entre-semana" more={t.sections.more} />
          ),
          summits: (
            <Section key="summits" title={t.sections.summits} sub={t.sections.summitsSub} testid="summits-section"
              filter={<DifficultyFilter value={diff} onChange={setDiff} />} items={summits} link="/tours?category=cumbre" more={t.sections.more} />
          ),
          adventures: (
            <Section key="adventures" title={t.sections.adventures} sub={t.sections.adventuresSub} testid="adventures-section"
              filter={<CategoryFilter value={cat} onChange={setCat} />} items={adventures} link="/tours" more={t.sections.more} />
          ),
          news: news.length > 0 && (
            <Section key="news" title={t.sections.news} sub={t.sections.newsSub} testid="news-section"
              items={news} link="/tours" more={t.sections.more} />
          ),
          bundles: <BundlesSection key="bundles" />,
        };
        return order.map((k) => blocks[k]).filter(Boolean);
      })()}

      <NewsBlock />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24" data-testid="destinations-map-section">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#16221D]">
          {lang === "es" ? "Encuentra tu próxima aventura" : "Find your next adventure"}
        </h2>
        <p className="mt-2 text-[#5E6D64] text-sm md:text-base">
          {lang === "es"
            ? "Visitamos diferentes zonas de México. Da clic en un estado para ver sus destinos."
            : "We explore different regions of Mexico. Tap a state to see its destinations."}
        </p>
        <div className="mt-8"><MexicoMap showFilter testid="home-mexico-map" /></div>
      </section>

      <section className="relative mt-24 min-h-[420px] flex items-center" data-testid="brand-band">
        <img src={thumb(BAND, 1280)} alt="" srcSet={srcSet(BAND)} sizes="100vw" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#16221D]/60" />
        <div className="relative max-w-3xl mx-auto px-6 py-20 text-white">
          <h2 className="font-heading uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight">{t.sections.loveTitle}</h2>
          <p className="mt-5 text-white/85 leading-relaxed">{t.sections.loveText} <strong>{t.sections.loveBold}</strong></p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="private-tour-section">
        <div className="rounded-3xl bg-[#16221D] text-white p-8 md:p-12 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <p className="text-[11px] uppercase tracking-widest text-[#F0C674]">
              {lang === "es" ? "Grupos y fechas a tu medida" : "Custom groups and dates"}
            </p>
            <h2 className="mt-3 font-heading uppercase text-3xl sm:text-4xl leading-none">
              {lang === "es" ? "¿Quieres un tour privado? Cotízalo con nosotros" : "Want a private tour? Get a quote"}
            </h2>
            <p className="mt-3 text-white/75 text-sm md:text-base">
              {lang === "es"
                ? "Armamos la ruta, la fecha y los servicios extra para tu grupo, empresa o familia."
                : "We build the route, date and extra services for your group, company or family."}
            </p>
          </div>
          <button onClick={() => setQuoteOpen(true)} data-testid="private-tour-cta"
            className="rounded-full bg-[#D94E28] text-white font-semibold px-8 py-3.5 hover:bg-[#B83B1A] transition-colors duration-300">
            {lang === "es" ? "Cotiza tu ruta privada" : "Quote your private trip"}
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="reviews-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#16221D]">
              {lang === "es" ? "Reseñas" : "Reviews"}
            </h2>
            {reviews?.rating && (
              <p className="mt-2 text-sm text-[#5E6D64]">
                {`${reviews.rating} ★ · ${reviews.user_rating_count} ${lang === "es" ? "calificaciones" : "ratings"}`}
              </p>
            )}
          </div>
          {reviews?.maps_url && (
            <a href={reviews.maps_url} target="_blank" rel="noopener noreferrer" data-testid="google-maps-link"
              className="rounded-full border border-[#6E7E56] text-[#6E7E56] px-5 py-2 text-sm hover:bg-[#6E7E56] hover:text-white transition-colors duration-300">
              {lang === "es" ? "Ver en Google Maps" : "View on Google Maps"}
            </a>
          )}
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {(reviews?.reviews || []).slice(0, 6).map((r, i) => (
            <div key={`${r.author}-${i}`} data-testid={`review-card-${i}`} className="rounded-2xl bg-white border border-[#E2DFD7] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
              <div className="flex items-center gap-3">
                {r.photo ? (
                  <img src={r.photo} alt="" loading="lazy" decoding="async" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <span className="h-10 w-10 rounded-full bg-[#6E7E56] text-white flex items-center justify-center font-semibold">{r.author?.[0]}</span>
                )}
                <div>
                  <p className="font-semibold text-[#16221D] text-sm">{r.author}</p>
                  <p className="text-xs text-[#D97706]">{"★".repeat(r.rating || 5)}</p>
                </div>
              </div>
              <p className="mt-4 text-sm text-[#3D4A43] leading-relaxed">{r.text}</p>
              {r.review_url && (
                <a href={r.review_url} target="_blank" rel="noopener noreferrer"
                  className="mt-4 inline-block text-xs text-[#6E7E56] underline">
                  {lang === "es" ? "Ver reseña en Google" : "See review on Google"}
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      <InstagramFeed social={social} />

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
      <Footer />
    </div>
  );
}

const Section = ({ title, sub, filter, items, testid, availability = false }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24" data-testid={testid}>
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#16221D]">{title}</h2>
    <p className="mt-2 text-[#5E6D64] text-sm md:text-base">{sub}</p>
    {filter && <div className="mt-6">{filter}</div>}
    <div className="mt-8">
      <CardCarousel perView={4} testid={`${testid}-carousel`}>
        {items.map((r, i) => <RouteCard key={r.route_id} route={r} index={i} availability={availability} />)}
      </CardCarousel>
    </div>
  </section>
);
